import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Card } from '@/components/Card';
import { CardHeader } from '@/components/CardHeader';
import { Save } from 'lucide-react';
import { me } from '@/lib/auth';
import { apiGet, apiPatch } from '@/features/_shared/queries';
import { QueryState } from '@/features/_shared/QueryState';

type DnsProvider = 'manual' | 'powerdns' | 'cloudflare';

type WeeklyDay = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

type TenantSettings = {
  id: string;
  slug: string;
  name: string;
  contact_email?: string;
  notes?: string;
  weekly_enabled?: boolean;
  weekly_report_day?: WeeklyDay;
  monthly_enabled?: boolean;
  monthly_report_day_of_month?: number;
  report_timezone?: string;
  dns_provider?: DnsProvider;
  custom_logo_path?: string;
};

type Tab = 'general' | 'cadence' | 'dns' | 'branding';

const TAB_LABELS: Record<Tab, string> = {
  general: 'Algemeen',
  cadence: 'Rapport-cadans',
  dns: 'DNS provider',
  branding: 'Branding',
};

const WEEKLY_DAYS: Array<{ value: WeeklyDay; label: string }> = [
  { value: 'monday', label: 'Maandag' },
  { value: 'tuesday', label: 'Dinsdag' },
  { value: 'wednesday', label: 'Woensdag' },
  { value: 'thursday', label: 'Donderdag' },
  { value: 'friday', label: 'Vrijdag' },
  { value: 'saturday', label: 'Zaterdag' },
  { value: 'sunday', label: 'Zondag' },
];

export function SettingsScreen({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const meQ = useQuery({ queryKey: ['auth', 'me'], queryFn: () => me() });
  const canWrite = meQ.data?.platform_role === 'admin' || meQ.data?.platform_role === 'operator';

  const tenantQ = useQuery({
    queryKey: ['tenant-settings', slug],
    queryFn: () => apiGet<TenantSettings>(`/tenants/${slug}`, { tenant: slug }),
  });

  const [tab, setTab] = useState<Tab>('general');
  const [draft, setDraft] = useState<Partial<TenantSettings>>({});

  useEffect(() => {
    if (tenantQ.data) {
      setDraft(tenantQ.data);
    }
  }, [tenantQ.data]);

  const saveMut = useMutation({
    mutationFn: (patch: Partial<TenantSettings>) =>
      apiPatch<TenantSettings>(`/tenants/${slug}`, patch, { tenant: slug }),
    onSuccess: (data) => {
      qc.setQueryData(['tenant-settings', slug], data);
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  const save = () => {
    saveMut.mutate(draft);
  };

  return (
    <div className="max-w-[900px] space-y-5 p-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-slate-900">Instellingen</h1>
          <p className="mt-0.5 text-[13px] text-slate-500">
            Tenant-configuratie voor {tenantQ.data?.name ?? slug}
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={save}
            disabled={saveMut.isPending}
            className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-[12px] font-medium text-slate-900 hover:bg-brand-500 disabled:opacity-50"
          >
            <Save size={12} />
            {saveMut.isPending ? 'Opslaan…' : 'Opslaan'}
          </button>
        )}
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200">
        {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-3 py-2 text-[12px] ${
              tab === t
                ? 'border-brand-600 font-semibold text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <QueryState
        isLoading={tenantQ.isLoading}
        isError={tenantQ.isError}
        onRetry={() => tenantQ.refetch()}
      >
        {saveMut.isSuccess && (
          <div
            role="status"
            className="rounded border border-green-200 bg-green-50 px-3 py-2 text-[12px] text-green-700"
          >
            Wijzigingen opgeslagen.
          </div>
        )}
        {saveMut.isError && (
          <div
            role="alert"
            className="rounded border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700"
          >
            Opslaan mislukt. Probeer opnieuw.
          </div>
        )}

        {tab === 'general' && (
          <Card>
            <CardHeader title="Algemeen" subtitle="Tenant-identiteit en contactgegevens" />
            <div className="grid grid-cols-2 gap-4 p-4">
              <Field
                label="Naam"
                value={draft.name ?? ''}
                disabled={!canWrite}
                onChange={(v) => setDraft((d) => ({ ...d, name: v }))}
              />
              <Field
                label="Slug"
                value={draft.slug ?? ''}
                disabled
                hint="Onveranderlijk na aanmaak."
                onChange={() => {}}
              />
              <Field
                label="Contact-e-mail"
                type="email"
                value={draft.contact_email ?? ''}
                disabled={!canWrite}
                onChange={(v) => setDraft((d) => ({ ...d, contact_email: v }))}
              />
              <Field
                label="Notities"
                value={draft.notes ?? ''}
                disabled={!canWrite}
                onChange={(v) => setDraft((d) => ({ ...d, notes: v }))}
                textarea
              />
            </div>
          </Card>
        )}

        {tab === 'cadence' && (
          <Card>
            <CardHeader
              title="Rapport-cadans"
              subtitle="Wanneer worden wekelijkse en maandelijkse rapporten verstuurd"
            />
            <div className="space-y-4 p-4">
              <Checkbox
                label="Wekelijks rapport actief"
                checked={!!draft.weekly_enabled}
                disabled={!canWrite}
                onChange={(v) => setDraft((d) => ({ ...d, weekly_enabled: v }))}
              />
              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="Wekelijkse dag"
                  value={draft.weekly_report_day ?? 'monday'}
                  disabled={!canWrite || !draft.weekly_enabled}
                  options={WEEKLY_DAYS.map((d) => ({
                    value: d.value,
                    label: d.label,
                  }))}
                  onChange={(v) =>
                    setDraft((d) => ({
                      ...d,
                      weekly_report_day: v as WeeklyDay,
                    }))
                  }
                />
                <Field
                  label="Tijdzone"
                  value={draft.report_timezone ?? 'Europe/Brussels'}
                  disabled={!canWrite}
                  onChange={(v) => setDraft((d) => ({ ...d, report_timezone: v }))}
                />
              </div>
              <div className="border-t border-slate-100 pt-4">
                <Checkbox
                  label="Maandrapport actief"
                  checked={!!draft.monthly_enabled}
                  disabled={!canWrite}
                  onChange={(v) => setDraft((d) => ({ ...d, monthly_enabled: v }))}
                />
                <Field
                  className="mt-3 max-w-[200px]"
                  label="Dag van de maand"
                  type="number"
                  min={1}
                  max={28}
                  value={String(draft.monthly_report_day_of_month ?? 1)}
                  disabled={!canWrite || !draft.monthly_enabled}
                  onChange={(v) =>
                    setDraft((d) => ({
                      ...d,
                      monthly_report_day_of_month: parseInt(v, 10) || 1,
                    }))
                  }
                />
              </div>
            </div>
          </Card>
        )}

        {tab === 'dns' && (
          <Card>
            <CardHeader title="DNS provider" subtitle="Hoe SerSo de klant-DNS-records publiceert" />
            <div className="space-y-3 p-4">
              <Select
                label="Provider"
                value={draft.dns_provider ?? 'manual'}
                disabled={!canWrite}
                options={[
                  { value: 'manual', label: 'Handmatig (instructies per e-mail)' },
                  { value: 'powerdns', label: 'PowerDNS API' },
                  { value: 'cloudflare', label: 'Cloudflare API' },
                ]}
                onChange={(v) => setDraft((d) => ({ ...d, dns_provider: v as DnsProvider }))}
              />
              <p className="text-[12px] leading-relaxed text-slate-500">
                Credentials voor PowerDNS / Cloudflare worden geconfigureerd in een aparte
                secret-vault; hier staat de provider-keuze.
              </p>
            </div>
          </Card>
        )}

        {tab === 'branding' && (
          <Card>
            <CardHeader
              title="Branding"
              subtitle="Optionele klant-eigen logo voor rapporten en mails"
            />
            <div className="space-y-3 p-4">
              <Field
                label="Pad naar custom logo"
                value={draft.custom_logo_path ?? ''}
                disabled={!canWrite}
                placeholder="/srv/dmarc/branding/{slug}/logo.png"
                onChange={(v) => setDraft((d) => ({ ...d, custom_logo_path: v }))}
                hint="Leeg laten = SerSo-logo gebruiken."
              />
            </div>
          </Card>
        )}
      </QueryState>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  disabled,
  textarea,
  hint,
  placeholder,
  min,
  max,
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'email' | 'number';
  disabled?: boolean;
  textarea?: boolean;
  hint?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 text-[12px] ${className ?? ''}`}>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </span>
      {textarea ? (
        <textarea
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder={placeholder}
          className="rounded border border-slate-200 bg-white px-2 py-1.5 text-[13px] focus:border-slate-400 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500"
        />
      ) : (
        <input
          type={type}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          min={min}
          max={max}
          className="h-8 rounded border border-slate-200 bg-white px-2 text-[13px] focus:border-slate-400 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500"
        />
      )}
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

function Select<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1 text-[12px]">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as T)}
        className="h-8 rounded border border-slate-200 bg-white px-2 text-[13px] focus:border-slate-400 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-[13px] text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}
